if (!customElements.get("variant-selects")) {
  customElements.define(
    "variant-selects",
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        setTimeout(() => {
        this.initAllSelectedOptions()
        });
      }

      get selectedOptionValues() {
        return Array.from(new Set(
          Array.from(
            this.querySelectorAll(
              "select option[selected], fieldset input:checked"
            )
          ).map(({ dataset }) => dataset.optionValueId).filter(Boolean)
        ));
      }

      get optionValues() {
        return Array.from(new Set(
          Array.from(
            this.querySelectorAll(
              "fieldset input"
            )
          ).map(({ dataset }) => dataset.optionValueId).filter(Boolean)
        ));
      }

      get dependentOptionValues() {
        return this.dataset.dependentOptionValues === "true";
      }

      get productVariants() {
        return this.parseJsonScript("[data-product-variants]", []);
      }

      get selectedVariant() {
        return this.parseJsonScript("[data-selected-variant]", null);
      }

      get productOptionValues() {
        return this.parseJsonScript("[data-product-option-values]", []);
      }

      get prefetchOptionValueSets() {
        if (!this.dependentOptionValues) {
          return this.optionValues.map((optionValueId) => [optionValueId]);
        }

        const primaryOption = this.productOptionValues.find(({ position }) => Number(position) === 1);
        if (!primaryOption?.values?.length) return [];

        const optionValueSets = primaryOption.values
          .map(({ name }) => this.getSelectionForPrimaryValue(name)?.optionValueIds)
          .filter((optionValueIds) => optionValueIds?.length);

        return Array.from(
          new Map(optionValueSets.map((optionValueIds) => [optionValueIds.join(","), optionValueIds])).values()
        );
      }

      parseJsonScript(selector, fallbackValue) {
        const json = this.querySelector(selector)?.textContent;
        if (!json) return fallbackValue;

        try {
          return JSON.parse(json);
        } catch (error) {
          return fallbackValue;
        }
      }

      getOptionValueIdsForVariant(variant) {
        if (!variant?.options?.length || !this.productOptionValues.length) return [];

        const optionValueIds = variant.options.map((optionName, index) => {
          const option = this.productOptionValues.find(({ position }) => Number(position) === index + 1);
          const optionValue = option?.values?.find(({ name }) => name === optionName);
          return optionValue?.id ? String(optionValue.id) : null;
        });

        return optionValueIds.every(Boolean) ? optionValueIds : [];
      }

      getSelectionForPrimaryValue(primaryValue) {
        if (!primaryValue) return null;

        const currentOptions = this.selectedVariant?.options ?? [];
        const matchingVariants = this.productVariants.filter(
          (variant) => variant.options?.[0] === primaryValue
        );
        const matchesCurrentOptions = (variant) =>
          variant.options?.slice(1).every((optionValue, index) => optionValue === currentOptions[index + 1]);

        const variant =
          matchingVariants.find((candidate) => candidate.available && matchesCurrentOptions(candidate)) ||
          matchingVariants.find((candidate) => candidate.available) ||
          matchingVariants.find(matchesCurrentOptions) ||
          matchingVariants[0];
        const optionValueIds = this.getOptionValueIdsForVariant(variant);

        return variant && optionValueIds.length ? { variant, optionValueIds } : null;
      }

      getSelectionForTarget(target) {
        if (!this.dependentOptionValues || !target?.closest(".product-form__input--dependent-primary")) {
          return null;
        }

        return this.getSelectionForPrimaryValue(target.value);
      }

      initAllSelectedOptions() {
        const initedOptionsIds = Array.from(this.querySelectorAll(
            "select option[selected], fieldset input:checked"
          )).map(({ dataset }) => dataset.optionValueId)
        initedOptionsIds.forEach((id) => {
          const doms = this.querySelectorAll(`input[data-option-value-id="${id}"]`)
          if (doms.length) {
            doms.forEach((dom) => {
              if (dom.checked) return
              dom.classList.add('active')
            })
          }
        })
      }

      getInputForEventTarget(target) {
        return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener("change", (event) => {
          const target = this.getInputForEventTarget(event.target);
          const resolvedSelection = this.getSelectionForTarget(target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            {
              data: {
                event,
                target,
                selectedOptionValues: resolvedSelection?.optionValueIds ?? this.selectedOptionValues,
                from: "variantSelect",
              },
            }
          );
        });

        if (this.dependentOptionValues) {
          requestAnimationFrame(() => this.ensureCompatibleOptionSelection());
        }
      }

      ensureCompatibleOptionSelection() {
        const radioGroups = new Map();

        this.querySelectorAll('fieldset input[type="radio"]').forEach((input) => {
          if (!radioGroups.has(input.name)) radioGroups.set(input.name, []);
          radioGroups.get(input.name).push(input);
        });

        let changedTarget = null;

        radioGroups.forEach((inputs) => {
          const selectedInput = inputs.find((input) => input.checked);
          if (selectedInput) return;

          const firstCompatibleInput = inputs[0];
          if (!firstCompatibleInput) return;

          firstCompatibleInput.checked = true;
          changedTarget = firstCompatibleInput;
        });

        this.querySelectorAll("select").forEach((select) => {
          const selectedOption = Array.from(select.options).find((option) => option.hasAttribute("selected"));
          if (selectedOption) return;

          const firstCompatibleOption = select.options[0];
          if (!firstCompatibleOption) return;

          Array.from(select.options).forEach((option) => option.removeAttribute("selected"));
          firstCompatibleOption.selected = true;
          firstCompatibleOption.setAttribute("selected", "selected");
          changedTarget = select;
        });

        if (changedTarget) {
          changedTarget.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === "SELECT" && target.selectedOptions.length) {
          const previouslySelectedOption = Array.from(target.options)
            .find((option) => option.getAttribute("selected"));
          previouslySelectedOption?.removeAttribute("selected");
          target.selectedOptions[0].setAttribute("selected", "selected");

          const swatchValue =
            target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest(".product-form__input")
            .querySelector("[data-selected-value] > .swatch");
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty(
              "--swatch--background",
              swatchValue
            );
            selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
          } else {
            selectedDropdownSwatchValue.style.setProperty(
              "--swatch--background",
              "unset"
            );
            selectedDropdownSwatchValue.classList.add("swatch--unavailable");
          }

          selectedDropdownSwatchValue.style.setProperty(
            "--swatch-focal-point",
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
          );
        } else if (tagName === "INPUT" && target.type === "radio") {
          const selectedSwatchValue = target
            .closest(`.product-form__input`)
            .querySelector("[data-selected-value]");
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}


if (!customElements.get("variant-picker-tabs")) {
  customElements.define(
    "variant-picker-tabs",
    class VariantPickerTabs extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        if (this.initialized) return
        this.initialized = true
        requestAnimationFrame(() => this.init())
      }

      init() {
        this.tabHeaderWrapperDom = this.querySelector('.product-variant-picker-tab-header-wrapper')
        this.tabHeaderContentDom = this.querySelector('.product-variant-picker-tab-header')
        this.tabLeftButtonDom = this.querySelector('.left-button')
        this.tabRightButtonDom = this.querySelector('.right-button')
        this.tabButtonsDom = this.querySelector('.switch-buttons')
        this.tabTitleDoms = this.querySelectorAll('.tab-header-title')
        this.showAllTab = this.dataset.showAllTab !== 'false'

        if (!this.tabHeaderWrapperDom) return
        const { scrollWidth, offsetWidth } = this.tabHeaderWrapperDom
        if (scrollWidth > offsetWidth) {
          this.classList.add('scrollable')
        }
        this.tabLeftButtonDom?.addEventListener('click', this.handleToLeft.bind(this))
        this.tabRightButtonDom?.addEventListener('click', this.handleToRight.bind(this))
        this.tabTitleDoms.forEach((dom, index) => {
          dom.addEventListener('click', () => this.applyActiveTab(index, { selectFirst: true }))
        })
        const forceFirstTab = this.dataset.forceFirstTab === 'true'
        window.shopifyTmpVars = window.shopifyTmpVars || {}
        if (forceFirstTab) {
          window.shopifyTmpVars.productVariantPickerTabActiveIndex = 0
        }
        const savedIndex = forceFirstTab
          ? 0
          : (window.shopifyTmpVars.productVariantPickerTabActiveIndex ?? 0)
        this.applyActiveTab(savedIndex)
      }

      applyActiveTab(index = 0, { selectFirst = false } = {}) {
        if (!this.tabTitleDoms) return
        const lastIndex = Math.max(this.tabTitleDoms.length - 1, 0)
        let activeIndex = Number(index)
        if (!Number.isFinite(activeIndex) || activeIndex < 0 || activeIndex > lastIndex) {
          activeIndex = 0
        }

        this.tabTitleDoms.forEach((dom, tabIndex) => {
          dom.classList.toggle('active', tabIndex === activeIndex)
        })

        window.shopifyTmpVars = window.shopifyTmpVars || {}
        window.shopifyTmpVars.productVariantPickerTabActiveIndex = activeIndex

        const isAllTab = this.showAllTab && activeIndex === 0
        const categoryIndex = this.showAllTab ? activeIndex - 1 : activeIndex

        this.querySelectorAll('fieldset input[data-option-value-id]').forEach((input) => {
          const tabs = (input.dataset.variantTabs || '').split(/\s+/).filter(Boolean)
          const visible = isAllTab || tabs.includes(String(categoryIndex))
          input.classList.toggle('is-tab-hidden', !visible)
        })

        if (selectFirst) {
          this.selectFirstVisibleShade()
        }

        this.dispatchEvent(
          new CustomEvent('variant-tabs:change', {
            bubbles: true,
            detail: { activeIndex, isAllTab, categoryIndex, selectFirst },
          })
        )
      }

      selectFirstVisibleShade() {
        const visibleInputs = Array.from(
          this.querySelectorAll('fieldset input[type="radio"][data-option-value-id]')
        ).filter((input) => (
          !input.classList.contains('is-tab-hidden')
          && !input.classList.contains('is-pcs-hidden')
        ))
        if (!visibleInputs.length) return

        const activeIndex = Number(window.shopifyTmpVars?.productVariantPickerTabActiveIndex ?? 0)
        const isAllTab = this.showAllTab && activeIndex === 0
        const categoryIndex = this.showAllTab ? activeIndex - 1 : activeIndex
        const colorSortOrder = this.getColorSortOrder()

        const pickFirstAvailable = (shadeOrder) => {
          for (const shadeName of shadeOrder) {
            const match = visibleInputs.find((input) => (
              String(input.value || '').trim() === String(shadeName || '').trim()
            ))
            // 不可售：HTML disabled 或 class disabled（售罄常只有后者）顺延下一个
            if (
              !match
              || match.disabled
              || match.classList.contains('disabled')
            ) continue
            return match
          }
          return null
        }

        let firstInput = null

        // 命中全局色号排序：在当前分类可见色号里，按排序配置取第一个可售
        if (colorSortOrder.length) {
          firstInput = pickFirstAvailable(colorSortOrder)
        } else if (!isAllTab && categoryIndex >= 0) {
          // 未命中排序：按分类配置原顺序取第一个可售
          const categoryOrders = this.getCategoryShadeOrders()
          const shadeOrder = Array.isArray(categoryOrders[categoryIndex])
            ? categoryOrders[categoryIndex]
            : []
          firstInput = pickFirstAvailable(shadeOrder)
        }

        if (!firstInput) {
          firstInput = visibleInputs.find((input) => (
            !input.disabled && !input.classList.contains('disabled')
          )) || visibleInputs[0]
        }
        if (!firstInput || firstInput.checked) return

        firstInput.checked = true
        firstInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      getColorSortOrder() {
        if (this._colorSortOrder) return this._colorSortOrder
        const json = this.querySelector('[data-color-sort-order]')?.textContent
        if (!json) {
          this._colorSortOrder = []
          return this._colorSortOrder
        }
        try {
          const parsed = JSON.parse(json)
          this._colorSortOrder = Array.isArray(parsed) ? parsed : []
        } catch (error) {
          this._colorSortOrder = []
        }
        return this._colorSortOrder
      }

      getCategoryShadeOrders() {
        if (this._categoryShadeOrders) return this._categoryShadeOrders
        const json = this.querySelector('[data-category-shade-orders]')?.textContent
        if (!json) {
          this._categoryShadeOrders = []
          return this._categoryShadeOrders
        }
        try {
          this._categoryShadeOrders = JSON.parse(json)
        } catch (error) {
          this._categoryShadeOrders = []
        }
        return this._categoryShadeOrders
      }

      handleToLeft() {
        const { scrollWidth, offsetWidth, scrollLeft } = this.tabHeaderWrapperDom
        if (scrollLeft > 0) {
          const newScrollLeft = Math.max(scrollLeft - 150,0)
          this.tabHeaderWrapperDom.scrollTo({left: newScrollLeft, behavior: 'smooth' })
          if (newScrollLeft === 0) {
            this.tabLeftButtonDom.classList.add('disabled')
          }
          this.tabRightButtonDom.classList.remove('disabled')
        }
      }

      handleToRight() {
        const { scrollWidth, offsetWidth, scrollLeft } = this.tabHeaderWrapperDom
        if (scrollLeft + offsetWidth < scrollWidth) {
          const newScrollLeft = Math.min(scrollLeft + 150,scrollWidth - offsetWidth)
          this.tabHeaderWrapperDom.scrollTo({left: newScrollLeft, behavior: 'smooth' })
          if (newScrollLeft >= scrollWidth - offsetWidth) {
            this.tabRightButtonDom.classList.add('disabled')
          }
          this.tabLeftButtonDom.classList.remove('disabled')
        }
      }
    }
  )}
